import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import { useRBAC } from "@/lib/rbac/context"
import type { ResourceType } from "@/lib/rbac/types"

// Hook to check page access
export const usePageAccess = (pageName: string): boolean => {
  const { hasPermission, isLoading } = useRBAC()
  const navigate = useNavigate()

  const hasAccess = !isLoading && hasPermission("page", pageName, "view")

  useEffect(() => {
    if (!isLoading && !hasAccess) {
      // Redirect to unauthorized page or dashboard
      navigate("/unauthorized")
    }
  }, [hasAccess, isLoading, navigate])

  return hasAccess
}

// Hook to check component access
export const useComponentAccess = (componentName: string, action?: string): boolean => {
  const { hasPermission, isLoading } = useRBAC()
  return !isLoading && hasPermission("component", componentName, action)
}

// Hook to check attribute access
export const useAttributeAccess = (attributeName: string, action?: string): boolean => {
  const { hasPermission, isLoading } = useRBAC()
  return !isLoading && hasPermission("attribute", attributeName, action)
}

// Hook to check any resource access
export const useResourceAccess = (resourceType: ResourceType, resourceName: string, action?: string): boolean => {
  const { hasPermission, isLoading } = useRBAC()
  return !isLoading && hasPermission(resourceType, resourceName, action)
}
